import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { savedReportsAPI } from '../services/api';
import { withEngineRetry } from '../utils/engineRetry';

const errorText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

/**
 * Run a report and keep its results table: a saved report by its id, an
 * unsaved one (the editor, a widget being previewed, a ready-made report)
 * through the preview endpoint. Every view that shows a report uses this, so
 * they all show the same thing.
 *
 *   report          { id?, cubeQuery, display }
 *   dateRange       a period to apply instead of the report's own
 *   filters         dashboard filters [{ member, values }]
 *   limit           row cap for unsaved reports
 *   delayMs         wait before running after a change (the editor's typing)
 *   refreshMinutes  run again on this interval
 *   enabled         false to hold off (the report is not ready to run)
 *
 * Only the newest request may set the result, so a quick change of period
 * or filter is never overwritten by an older answer.
 */
export default function useReportResult({ companyId, report, dateRange = null, filters = [], limit = 1000, delayMs = 0, refreshMinutes = 0, enabled = true }) {
  const [state, setState] = useState({ loading: Boolean(enabled), error: null, result: null, at: null, startedAt: Date.now(), retrying: false, ms: null });
  const latest = useRef(0);
  // Colour rules and the target only change how results look, so they do not run the report again.
  const key = useMemo(() => JSON.stringify([
    report?.id || null,
    report?.id ? null : report?.cubeQuery || null,
    report?.id ? report?.display?.displayType : (({ colorRules, target, ...rest }) => rest)(report?.display || {}), // eslint-disable-line no-unused-vars
    report?.id ? report?.updatedAt || null : null,
    dateRange || null,
    filters || [],
    limit,
  ]), [report, dateRange, filters, limit]);

  const run = useCallback(async () => {
    if (!companyId || !report || !enabled) return;
    const id = ++latest.current;
    const started = performance.now();
    setState((prev) => ({ ...prev, loading: true, error: null, startedAt: Date.now(), retrying: false }));
    try {
      const response = await withEngineRetry(
        () => (report.id
          ? savedReportsAPI.run(report.id, companyId, dateRange || null, filters)
          : savedReportsAPI.preview(companyId, { cubeQuery: report.cubeQuery, display: report.display, dateRange: dateRange || null, filters }, limit)),
        { onRetry: () => { if (id === latest.current) setState((prev) => ({ ...prev, retrying: true })); } },
      );
      if (id !== latest.current) return;
      if (!response?.success) throw new Error(response?.error || 'The report could not run');
      setState({
        loading: false, error: null, result: response.data, retrying: false, startedAt: null,
        at: response.data.lastRefreshTime || new Date().toISOString(), ms: Math.round(performance.now() - started),
      });
    } catch (err) {
      if (id !== latest.current) return;
      setState((prev) => ({ ...prev, loading: false, retrying: false, error: errorText(err, 'The report could not run') }));
    }
  }, [companyId, key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled) { latest.current += 1; setState((prev) => ({ ...prev, loading: false })); return undefined; }
    const timer = setTimeout(run, delayMs);
    const interval = refreshMinutes > 0 ? setInterval(run, Math.max(1, refreshMinutes) * 60 * 1000) : null;
    return () => { clearTimeout(timer); if (interval) clearInterval(interval); };
  }, [run, delayMs, refreshMinutes, enabled]);

  return {
    ...state,
    table: state.result?.table || null,
    query: state.result?.query || null,
    run,
  };
}
